import { inject, Injectable, OnDestroy } from '@angular/core';
import { catchError, map, of, Subject, takeUntil, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  BrandsInterface,
  BrandsRequestInterface,
} from '../../interfaces/brands/brands-interface';
import { environment } from '../../../../environments/environment';
import { StoreService } from '../../store/store.service';
import {
  SearchV2RequestInterface,
  SearchV2ResponseInterface,
} from '@component-library';
import { GlobalMessageService } from '../global-services/global-message-service/global-message.service';
import { BrandsClass } from '../../classes/brands/brands-class';
import { BroadcastChannelService } from '../../broadcast-channel/services/broadcast-channel.service';

@Injectable({
  providedIn: 'root',
})
export class BrandsService implements OnDestroy {
  globalMessageService = inject(GlobalMessageService);
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private storeService: StoreService,
    private broadcastChannelService: BroadcastChannelService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAllBrandsRequest = (sendMessage: boolean) => {
    return this.http
      .get<BrandsRequestInterface>(
        `${environment.baseUrl}/brands/list?GetAll=true&Page=0&PageSize=999`
      )
      .pipe(
        catchError(() => {
          return of({
            Items: [],
            Status: false,
            Message: 'Markalar getirilemedi',
            TotalCount: 0,
            Page: 0,
            PageSize: 0,
            Exception: null,
          } as BrandsRequestInterface);
        }),
        tap((res) => {
          if (res.Status) {
            this.storeService.brands$.set(
              res.Items.map((f) => new BrandsClass(f))
            );
            if (sendMessage) {
              this.broadcastChannelService.brandTableChannelSend(res.Items);
            }
            return res.Items;
          }
          throw res.Message;
        })
      );
  };

  searchv2(searchv2: SearchV2RequestInterface) {
    return this.http
      .post<SearchV2ResponseInterface>(
        `${environment.baseUrl}/brands/searchV2`,
        searchv2
      )
      .pipe(
        catchError(() => {
          return of({
            CurrentPage: 0,
            CurrentPageSize: 0,
            Items: [],
            Message: 'Arama işlemi başarısız oldu',
            Status: false,
            TotalCount: 0,
          } as SearchV2ResponseInterface);
        }),
        map((response: SearchV2ResponseInterface) => {
          if (response && response.Status) {
            return {
              CurrentPage: response.CurrentPage || 0,
              CurrentPageSize: response.CurrentPageSize || 0,
              Items:
                response.Items.map((brand) => new BrandsClass(brand)) || [],
              Message: response.Message || '',
              Status: response.Status || false,
              TotalCount: response.TotalCount || 0,
            };
          }
          return {
            CurrentPage: 0,
            CurrentPageSize: 0,
            Items: [],
            Message: 'Arama işlemi başarısız oldu',
            Status: false,
            TotalCount: 0,
          };
        })
      );
  }

  addBrand = (brand: BrandsInterface) => {
    return this.http
      .post<BrandsInterface>(`${environment.baseUrl}/brands/add`, brand)
      .pipe(
        catchError(() => {
          return of(brand);
        }),
        tap((f) => {
          if (f) {
            this.globalMessageService.showStatusMessages({
              status: f.Id.length > 0,
              summary: 'Marka Ekleme',
              infoDetail: 'Marka Ekleme İşlemi Başarıyla Tamamlandı',
              errorDetail: 'Marka Ekleme İşlemi Başarısız.',
            });

            if (f.Id.length > 0) {
              this.refreshStore();
            }
          }
        })
      );
  };

  deleteBrand = (brand: BrandsInterface) => {
    return this.http
      .post<{ IsSuccess: boolean; Message: string }>(
        `${environment.baseUrl}/brands/delete`,
        brand
      )
      .pipe(
        catchError(() => {
          return of({ IsSuccess: false, Message: 'Marka silinemedi' });
        }),
        tap((f) => {
          if (f) {
            this.globalMessageService.showStatusMessages({
              status: f?.IsSuccess,
              summary: 'Marka Silme',
              infoDetail: `${brand.Name} Markası Başarıyla Silindi.`,
              errorDetail: `${brand.Name} Markası İçin Silme İşlemi Başarısız Oldu.`,
            });

            if (f?.IsSuccess) {
              this.refreshStore();
            }
          }
        })
      );
  };

  deleteBulk = (ids: string[]) => {
    return this.http
      .post<{ Status: boolean; Message: string }>(
        `${environment.baseUrl}/brands/deleteBulk`,
        { ids }
      )
      .pipe(
        catchError(() => {
          return of({ Status: false, Message: 'Toplu silme işlemi başarısız' });
        }),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Toplu Marka Sil',
            infoDetail: 'Toplu Marka Silme İşlemi Başarıyla Tamamlandı',
            errorDetail: 'Toplu Marka Silme İşlemi Başarısız.',
          });

          if (f && f.Status) {
            this.refreshStore();
          }
        })
      );
  };

  addBulk = (brands: BrandsInterface[]) => {
    return this.http
      .post<{ Brands: BrandsInterface[] }>(
        `${environment.baseUrl}/brands/addBulk`,
        { brands }
      )
      .pipe(
        catchError(() => {
          return of({ Brands: [] });
        }),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: !!f && f.Brands.length > 0,
            summary: 'Toplu Marka Ekle',
            infoDetail: 'Toplu Marka Ekleme İşlemi Başarıyla Tamamlandı',
            errorDetail: 'Toplu Marka Ekleme İşlemi Başarısız.',
          });

          if (f && f.Brands.length > 0) {
            this.refreshStore();
          }
        })
      );
  };

  updateBrand = (brand: BrandsInterface) => {
    return this.http
      .post<BrandsInterface>(`${environment.baseUrl}/brands/update`, brand)
      .pipe(
        catchError(() => {
          return of(null);
        }),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: !!f && f.Id.length > 0,
            summary: 'Marka Güncelleme',
            infoDetail: 'Marka Güncelleme İşlemi Başarıyla Tamamlandı',
            errorDetail: 'Marka Güncelleme İşlemi Başarısız.',
          });

          if (f && f.Id.length > 0) {
            this.refreshStore();
          }
        })
      );
  };

  refreshStore = () => {
    const subscription = this.getAllBrandsRequest(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        subscription.unsubscribe();
      });
  };
}
